import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Bell,
  CheckCheck,
  Trash2,
  X,
  FileText,
  CreditCard,
  UserCheck,
  Package,
  UserPlus,
  Shield,
  Settings2,
  ExternalLink,
  Clock,
  Sparkles,
  Inbox,
  Filter,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import {
  subscribeToNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification,
  clearAllNotifications,
  ensureRecentActivityNotifications,
  canUserSeeNotification,
  getPermittedCategoriesForUser,
} from '@/services/notificationService';
import type { Notification, NotificationCategory } from '@/types';
import { formatDistanceToNow } from 'date-fns';

interface NotificationCenterProps {
  onClose: () => void;
}

// Category Configuration with labels, colors, and premium Lucide SVGs
export const CATEGORY_CONFIG: Record<
  NotificationCategory | 'all',
  {
    label: string;
    icon: React.ComponentType<{ size?: number; className?: string }>;
    badgeClass: string;
    iconBgClass: string;
    textColor: string;
  }
> = {
  all: {
    label: 'All',
    icon: Bell,
    badgeClass: 'bg-gray-100 text-gray-700 border-gray-200',
    iconBgClass: 'bg-gray-100 text-gray-700',
    textColor: 'text-gray-700',
  },
  invoice: {
    label: 'Estimate',
    icon: FileText,
    badgeClass: 'bg-blue-50 text-blue-700 border-blue-200/70',
    iconBgClass: 'bg-blue-100/80 text-blue-700',
    textColor: 'text-blue-700',
  },
  payment: {
    label: 'Payment',
    icon: CreditCard,
    badgeClass: 'bg-emerald-50 text-emerald-700 border-emerald-200/70',
    iconBgClass: 'bg-emerald-100/80 text-emerald-700',
    textColor: 'text-emerald-700',
  },
  customer: {
    label: 'Customer',
    icon: UserCheck,
    badgeClass: 'bg-purple-50 text-purple-700 border-purple-200/70',
    iconBgClass: 'bg-purple-100/80 text-purple-700',
    textColor: 'text-purple-700',
  },
  inventory: {
    label: 'Inventory',
    icon: Package,
    badgeClass: 'bg-amber-50 text-amber-700 border-amber-200/70',
    iconBgClass: 'bg-amber-100/80 text-amber-700',
    textColor: 'text-amber-700',
  },
  staff: {
    label: 'Staff',
    icon: UserPlus,
    badgeClass: 'bg-cyan-50 text-cyan-700 border-cyan-200/70',
    iconBgClass: 'bg-cyan-100/80 text-cyan-700',
    textColor: 'text-cyan-700',
  },
  security: {
    label: 'Security',
    icon: Shield,
    badgeClass: 'bg-rose-50 text-rose-700 border-rose-200/70',
    iconBgClass: 'bg-rose-100/80 text-rose-700',
    textColor: 'text-rose-700',
  },
  settings: {
    label: 'Settings',
    icon: Settings2,
    badgeClass: 'bg-slate-50 text-slate-700 border-slate-200/70',
    iconBgClass: 'bg-slate-100/80 text-slate-700',
    textColor: 'text-slate-700',
  },
  system: {
    label: 'System',
    icon: Sparkles,
    badgeClass: 'bg-indigo-50 text-indigo-700 border-indigo-200/70',
    iconBgClass: 'bg-indigo-100/80 text-indigo-700',
    textColor: 'text-indigo-700',
  },
};

const NotificationCenter: React.FC<NotificationCenterProps> = ({ onClose }) => {
  const { appUser, companyId, isAdmin, can, canAny } = useAuthContext();
  const navigate = useNavigate();

  const [rawNotifications, setRawNotifications] = useState<Notification[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<NotificationCategory | 'all'>('all');
  const [filterUnreadOnly, setFilterUnreadOnly] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [swipedItemId, setSwipedItemId] = useState<string | null>(null);

  const touchStartXRef = useRef<number | null>(null);
  const touchCurrentXRef = useRef<number | null>(null);

  // Subscribe to real-time notifications
  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }

    ensureRecentActivityNotifications(companyId);

    const unsubscribe = subscribeToNotifications(companyId, (items) => {
      setRawNotifications(items);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [companyId]);

  // Filter notifications strictly based on the user's role and granted permissions
  const notifications = useMemo(() => {
    return rawNotifications.filter((n) =>
      canUserSeeNotification(n, { uid: appUser?.uid, isAdmin, can, canAny }),
    );
  }, [rawNotifications, appUser?.uid, isAdmin, can, canAny]);

  // Permitted categories list for tabs
  const categories = useMemo(() => {
    return getPermittedCategoriesForUser({ uid: appUser?.uid, isAdmin, can, canAny });
  }, [appUser?.uid, isAdmin, can, canAny]);

  // Counts for the user's permitted notifications
  const unreadCount = notifications.filter((n) => !n.read).length;

  // Filtered list based on selected category tab & unread filter
  const filteredNotifications = notifications.filter((n) => {
    const matchesCategory = selectedCategory === 'all' || n.category === selectedCategory;
    const matchesUnread = !filterUnreadOnly || !n.read;
    return matchesCategory && matchesUnread;
  });

  // Touch Swipe handlers for mobile swipe-to-reveal
  const handleTouchStart = (id: string, e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
    touchCurrentXRef.current = e.touches[0].clientX;
  };

  const handleTouchMove = (_id: string, e: React.TouchEvent) => {
    touchCurrentXRef.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (id: string) => {
    if (touchStartXRef.current !== null && touchCurrentXRef.current !== null) {
      const diff = touchStartXRef.current - touchCurrentXRef.current;
      if (diff > 35) {
        // Swiped left: open delete slider
        setSwipedItemId(id);
      } else if (diff < -20) {
        // Swiped right: close delete slider
        setSwipedItemId((prev) => (prev === id ? null : prev));
      }
    }
    touchStartXRef.current = null;
    touchCurrentXRef.current = null;
  };

  // Action handlers
  const handleItemClick = async (notification: Notification) => {
    if (swipedItemId === notification.id) {
      setSwipedItemId(null);
      return;
    }
    setSwipedItemId(null);
    if (companyId && !notification.read) {
      await markNotificationAsRead(companyId, notification.id);
    }
    if (notification.link) {
      navigate(notification.link);
      onClose();
    }
  };

  const handleMarkAllRead = async () => {
    if (companyId && notifications.length > 0) {
      await markAllNotificationsAsRead(companyId, notifications);
    }
  };

  const handleDelete = async (e: React.MouseEvent | React.TouchEvent, notificationId: string) => {
    e.stopPropagation();
    if (companyId) {
      // Trigger smooth sliding exit animation before database removal
      setDeletingIds((prev) => new Set(prev).add(notificationId));
      setTimeout(async () => {
        await deleteNotification(companyId, notificationId);
        setDeletingIds((prev) => {
          const next = new Set(prev);
          next.delete(notificationId);
          return next;
        });
      }, 260);
    }
  };

  const handleClearAll = async () => {
    if (companyId && notifications.length > 0) {
      if (window.confirm('Clear all notifications?')) {
        await clearAllNotifications(companyId);
      }
    }
  };

  const formatTimestamp = (timestamp: number) => {
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch {
      return 'Recently';
    }
  };

  const cleanNotificationText = (text: string): string => {
    if (!text) return '';
    // Strip raw Firebase ID strings in parentheses like "Adil (-P1538m42hYCTk88bOYl)" -> "Adil"
    return text.replace(/\s*\(-[A-Za-z0-9_-]+\)/g, '').trim();
  };

  return (
    <>
      {/* Mobile Backdrop Overlay */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-[1px] z-40 sm:hidden transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Main Notification Container */}
      <div className="fixed inset-x-3 top-16 sm:absolute sm:inset-auto sm:right-0 sm:top-12 sm:w-96 w-auto max-w-[calc(100vw-24px)] sm:max-w-none bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 animate-slide-up overflow-hidden flex flex-col max-h-[80vh] sm:max-h-[85vh]">
        {/* Header */}
        <div className="px-3.5 sm:px-4 py-3 sm:py-3.5 border-b border-gray-100 bg-gray-50/80 backdrop-blur-sm flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center shrink-0">
              <Bell size={15} />
            </div>
            <div className="min-w-0">
              <h4 className="text-xs font-bold text-gray-900 tracking-tight truncate">
                {isAdmin ? 'Admin Notification Stream' : 'Role Notifications'}
              </h4>
              <p className="text-[10px] text-gray-500 font-medium truncate">
                {unreadCount > 0 ? `${unreadCount} unread action${unreadCount > 1 ? 's' : ''}` : 'All caught up'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1 shrink-0">
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                title="Mark all as read"
                className="px-2 py-1 text-[11px] font-semibold text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors flex items-center gap-1 cursor-pointer"
              >
                <CheckCheck size={13} />
                <span>Read all</span>
              </button>
            )}

            {isAdmin && notifications.length > 0 && (
              <button
                onClick={handleClearAll}
                title="Clear all notifications"
                className="p-1.5 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
              >
                <Trash2 size={14} />
              </button>
            )}

            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200/60 rounded-lg transition-colors cursor-pointer ml-0.5"
            >
              <X size={15} />
            </button>
          </div>
        </div>

      {/* Category Tabs */}
      <div className="px-3 pt-2.5 pb-2 border-b border-gray-100 bg-white">
        <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar pb-1">
          {categories.map((catKey) => {
            const config = CATEGORY_CONFIG[catKey] || CATEGORY_CONFIG.system;
            const Icon = config.icon;
            const isSelected = selectedCategory === catKey;
            const count =
              catKey === 'all'
                ? notifications.length
                : notifications.filter((n) => n.category === catKey).length;

            if (catKey !== 'all' && count === 0) return null;

            return (
              <button
                key={catKey}
                onClick={() => setSelectedCategory(catKey)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                  isSelected
                    ? 'bg-primary-600 text-white shadow-xs'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-900 border border-gray-200/60'
                }`}
              >
                <Icon size={12} className={isSelected ? 'text-white' : config.textColor} />
                <span>{config.label}</span>
                {count > 0 && (
                  <span
                    className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                      isSelected ? 'bg-primary-700/80 text-white' : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Unread Filter Toggle */}
        <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-gray-50 text-[11px]">
          <span className="text-gray-400 font-medium">
            Showing {filteredNotifications.length} of {notifications.length}
          </span>
          <button
            onClick={() => setFilterUnreadOnly((p) => !p)}
            className={`font-semibold flex items-center gap-1 cursor-pointer transition-colors ${
              filterUnreadOnly ? 'text-primary-600' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Filter size={11} />
            <span>{filterUnreadOnly ? 'Showing unread' : 'Unread only'}</span>
          </button>
        </div>
      </div>

      {/* Notification List */}
      <div className="flex-1 overflow-y-auto divide-y divide-gray-50 max-h-[380px]">
        {loading ? (
          <div className="p-8 text-center text-gray-400 text-xs">Loading real-time updates...</div>
        ) : filteredNotifications.length === 0 ? (
          <div className="py-12 px-4 text-center">
            <div className="w-12 h-12 mx-auto rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center text-gray-400 mb-2.5">
              <Inbox size={22} className="stroke-[1.5]" />
            </div>
            <p className="text-xs font-bold text-gray-700">No notifications found</p>
            <p className="text-[11px] text-gray-400 mt-0.5">
              {filterUnreadOnly
                ? 'You have no unread notifications in this filter.'
                : 'Activity updates for your assigned role will appear here live.'}
            </p>
          </div>
        ) : (
          filteredNotifications.map((n) => {
            const config = CATEGORY_CONFIG[n.category] || CATEGORY_CONFIG.system;
            const Icon = config.icon;
            const isDeleting = deletingIds.has(n.id);
            const isSwiped = swipedItemId === n.id;

            return (
              <div
                key={n.id}
                className={`relative overflow-hidden transition-all duration-300 ease-in-out group ${
                  isDeleting
                    ? '-translate-x-full opacity-0 max-h-0 py-0'
                    : 'max-h-48 opacity-100'
                }`}
              >
                {/* Sliding Delete Action Panel (Revealed ONLY on Hover / Swipe) */}
                {isAdmin && (
                  <div
                    onClick={(e) => handleDelete(e, n.id)}
                    className={`absolute inset-y-0 right-0 w-16 bg-gradient-to-r from-rose-500 to-rose-600 flex flex-col items-center justify-center text-white cursor-pointer hover:from-rose-600 hover:to-rose-700 transition-all duration-200 z-10 select-none shadow-inner active:scale-95 ${
                      isSwiped
                        ? 'translate-x-0 opacity-100'
                        : 'translate-x-full opacity-0 sm:group-hover:translate-x-0 sm:group-hover:opacity-100'
                    }`}
                    title="Delete notification"
                  >
                    <Trash2 size={16} className="transition-transform group-hover:scale-110" />
                    <span className="text-[9px] font-bold mt-0.5 uppercase tracking-wider">Delete</span>
                  </div>
                )}

                {/* Main Notification Card that slides left on hover or swipe */}
                <div
                  onClick={() => handleItemClick(n)}
                  onTouchStart={(e) => handleTouchStart(n.id, e)}
                  onTouchMove={(e) => handleTouchMove(n.id, e)}
                  onTouchEnd={() => handleTouchEnd(n.id)}
                  className={`px-4 py-3 transition-all duration-200 ease-out cursor-pointer relative z-20 ${
                    isAdmin ? 'sm:group-hover:-translate-x-16' : ''
                  } ${isSwiped ? '-translate-x-16' : 'translate-x-0'} ${
                    !n.read ? 'bg-[#f4f7ff] sm:group-hover:bg-[#eef3ff]' : 'bg-white sm:group-hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {/* Category Icon Badge */}
                    <div
                      className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 shadow-2xs border border-white/60 ${config.iconBgClass}`}
                    >
                      <Icon size={15} />
                    </div>

                    {/* Body Content */}
                    <div className="flex-1 min-w-0 pr-2">
                      <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                        {/* Category Pill */}
                        <span
                          className={`inline-flex items-center gap-1 text-[9.5px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md border ${config.badgeClass}`}
                        >
                          <Icon size={9} />
                          {config.label}
                        </span>

                        {/* Title */}
                        <span className="text-xs font-bold text-gray-800 truncate">
                          {cleanNotificationText(n.title)}
                        </span>
                      </div>

                      {/* Message */}
                      <p className="text-xs text-gray-600 leading-snug break-words">
                        {cleanNotificationText(n.message)}
                      </p>

                      {/* Footer / Time */}
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="inline-flex items-center gap-1 text-[10.5px] text-gray-400 font-medium">
                          <Clock size={10} />
                          {formatTimestamp(n.createdAt)}
                        </span>

                        {n.link && (
                          <span className="inline-flex items-center gap-0.5 text-[10.5px] font-semibold text-primary-600 group-hover:underline">
                            View details <ExternalLink size={9} />
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Right side indicators */}
                    <div className="flex flex-col items-end gap-2 flex-shrink-0 pt-0.5">
                      {!n.read && (
                        <span
                          className="w-2 h-2 bg-primary-500 rounded-full animate-pulse shadow-xs"
                          title="Unread notification"
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="px-3.5 sm:px-4 py-2.5 bg-gray-50 border-t border-gray-100 flex items-center justify-between text-[11px] text-gray-500 gap-2">
          <span className="flex items-center gap-1 text-emerald-600 font-medium truncate">
            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-ping shrink-0" />
            <span className="truncate">Live Role Sync Active</span>
          </span>
          {isAdmin && (
            <button
              onClick={() => {
                navigate('/audit-logs');
                onClose();
              }}
              className="text-primary-600 font-semibold hover:underline cursor-pointer shrink-0 text-right"
            >
              System Audit Logs &rarr;
            </button>
          )}
        </div>
      )}
    </div>
    </>
  );
};

export default NotificationCenter;
